import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChatWidget from '../components/ChatWidget';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiHeart, FiDollarSign, FiShield, FiCheckCircle, FiArrowRight, 
  FiUser, FiUsers, FiClock, FiTrendingUp, FiHome, FiCalendar, 
  FiTarget, FiStar, FiBriefcase, FiX 
} = FiIcons;

const FinalExpensePage = () => {
  const benefits = [
    'Coverage from $1,000 to $30,000',
    'Guaranteed acceptance for ages 50-85',
    'No medical exam or health questions',
    'Immediate coverage available',
    'Fixed premiums that never increase',
    'Cash value accumulation over time'
  ];

  const whoShouldConsider = [
    {
      icon: FiUser,
      title: 'Seniors (Ages 50-85)',
      description: 'Older adults wanting to cover final expenses without burdening family',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      scenarios: [
        'Cover funeral and burial costs',
        'Pay off small debts',
        'Leave something for family',
        'No other life insurance options'
      ]
    },
    {
      icon: FiHeart,
      title: 'Health Issues',
      description: 'People with health problems who can\'t qualify for other coverage',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      scenarios: [
        'Previous insurance denials',
        'Chronic health conditions',
        'Taking multiple medications',
        'Recent medical procedures'
      ]
    },
    {
      icon: FiDollarSign,
      title: 'Limited Budget',
      description: 'Those who need coverage but have tight financial constraints',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      scenarios: [
        'Fixed income seniors',
        'Small burial fund needed',
        'Affordable monthly payments',
        'Simple application process'
      ]
    },
    {
      icon: FiClock,
      title: 'Immediate Need',
      description: 'People who need coverage quickly without delays',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      scenarios: [
        'Recent health diagnosis',
        'Family history concerns',
        'Want coverage today',
        'No waiting periods'
      ]
    }
  ];

  const whoShouldConsiderWhole = [
    {
      icon: FiStar,
      title: 'Higher Coverage Needs',
      description: 'Need more than $30,000 in coverage',
      reason: 'Coverage Limits',
      alternative: 'Whole life offers up to $100,000+ coverage'
    },
    {
      icon: FiTrendingUp,
      title: 'Cash Value Priority',
      description: 'Want significant cash value accumulation',
      reason: 'Investment Component',
      alternative: 'Whole life builds substantial cash value over time'
    },
    {
      icon: FiShield,
      title: 'Estate Planning',
      description: 'Using life insurance for wealth transfer',
      reason: 'Estate Strategy',
      alternative: 'Whole life offers tax advantages for estates'
    },
    {
      icon: FiHome,
      title: 'Younger Applicants (Under 60)',
      description: 'Have time to build cash value and pay lower premiums',
      reason: 'Age Advantage',
      alternative: 'Whole life premiums are lower when younger'
    }
  ];

  const whoShouldNotConsider = [
    {
      icon: FiBriefcase,
      title: 'Young Professionals (Under 50)',
      description: 'Have better, more affordable options available',
      reason: 'Better Alternatives',
      alternative: 'Term life insurance offers much more coverage for less cost'
    },
    {
      icon: FiUsers,
      title: 'Healthy Individuals',
      description: 'Can qualify for traditional life insurance',
      reason: 'Health Qualifications',
      alternative: 'Traditional policies offer better rates and more coverage'
    },
    {
      icon: FiTarget,
      title: 'High Coverage Needs',
      description: 'Need more than $30,000 in coverage',
      reason: 'Coverage Insufficient',
      alternative: 'Term or whole life insurance for larger amounts'
    },
    {
      icon: FiX,
      title: 'Investment Seekers',
      description: 'Looking for high returns on cash value',
      reason: 'Low Investment Returns',
      alternative: 'Separate life insurance and investments typically perform better'
    }
  ];

  const expenses = [
    { item: 'Funeral service', cost: '$2,000 - $4,000' },
    { item: 'Burial plot', cost: '$1,000 - $4,000' },
    { item: 'Casket', cost: '$2,000 - $10,000' },
    { item: 'Headstone/marker', cost: '$1,000 - $3,000' },
    { item: 'Cremation', cost: '$1,000 - $3,000' },
    { item: 'Memorial service', cost: '$500 - $2,000' },
    { item: 'Transportation', cost: '$300 - $700' },
    { item: 'Death certificates', cost: '$100 - $300' }
  ];

  const testimonials = [
    {
      name: 'Margaret H.',
      age: 67,
      location: 'Bakersfield, CA',
      quote: 'I didn\'t want my children to worry about funeral costs. This policy gives me peace of mind knowing everything will be taken care of.',
      coverage: '$15,000 Final Expense'
    },
    {
      name: 'William T.',
      age: 73,
      location: 'Modesto, CA',
      quote: 'The application was so simple - no medical exam, just a few questions. Now my wife won\'t have to use our savings for my final expenses.',
      coverage: '$10,000 Final Expense'
    },
    {
      name: 'Rose M.',
      age: 71,
      location: 'Stockton, CA',
      quote: 'After seeing what my sister\'s family went through with funeral costs, I knew I needed this coverage. The premiums are very affordable.',
      coverage: '$20,000 Final Expense'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-600 to-red-800 pt-24 pb-16 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="flex items-center mb-4">
                <SafeIcon icon={FiHeart} className="h-8 w-8 mr-3" />
                <span className="font-semibold">Final Expense Insurance</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Don't Leave Final Expenses to Your Family
              </h1>
              <p className="text-xl text-red-100 mb-8">
                Cover funeral costs, burial expenses, and final medical bills with guaranteed acceptance life insurance. No medical exam required.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://quote.burbankinsuranceservices.com/Dt2tCn"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-8 py-4 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-300 transition-colors"
                >
                  Get My Price
                  <SafeIcon icon={FiArrowRight} className="h-5 w-5 ml-2" />
                </a>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-lg p-8 text-secondary-900"
            >
              <h3 className="text-2xl font-bold mb-6">Average Final Expenses</h3>
              <div className="space-y-3">
                {expenses.slice(0, 6).map((expense, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-secondary-200">
                    <span className="text-secondary-700">{expense.item}</span>
                    <span className="font-medium text-red-600">{expense.cost}</span>
                  </div>
                ))}
                <div className="border-t-2 border-red-600 pt-3 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-lg">Total Average Cost</span>
                    <span className="font-bold text-lg text-red-600">$7,500 - $25,000</span>
                  </div>
                </div>
              </div>
              <p className="text-sm text-secondary-600 mt-4">
                *Costs vary by location and service choices
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who Should Consider Final Expense - Infographic */}
      <section className="py-16 bg-gradient-to-br from-secondary-50 to-red-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Is Final Expense Insurance Right for You?
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              Final expense insurance serves a specific purpose for specific people. Here's who benefits most, who should consider whole life instead, and who may have better options.
            </p>
          </motion.div>

          {/* Who SHOULD Consider Final Expense */}
          <div className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <h3 className="text-2xl font-bold text-red-700 mb-2">✅ Perfect Candidates for Final Expense Insurance</h3>
              <p className="text-secondary-600">These situations make final expense insurance the ideal choice</p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whoShouldConsider.map((candidate, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`${candidate.bgColor} ${candidate.borderColor} border-2 rounded-xl p-6 hover:shadow-lg transition-shadow`}
                >
                  <div className="text-center mb-4">
                    <div className={`${candidate.bgColor} rounded-full p-3 w-16 h-16 mx-auto mb-3 flex items-center justify-center border ${candidate.borderColor}`}>
                      <SafeIcon icon={candidate.icon} className={`h-8 w-8 ${candidate.color}`} />
                    </div>
                    <h4 className="font-bold text-secondary-900 mb-2">{candidate.title}</h4>
                    <p className="text-sm text-secondary-600 mb-4">{candidate.description}</p>
                  </div>
                  <div>
                    <h5 className="font-semibold text-secondary-800 mb-2 text-sm">Perfect for:</h5>
                    <ul className="space-y-1">
                      {candidate.scenarios.map((scenario, idx) => (
                        <li key={idx} className="text-xs text-secondary-700 flex items-center">
                          <SafeIcon icon={FiCheckCircle} className={`h-3 w-3 ${candidate.color} mr-2`} />
                          {scenario}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Who Should Consider Whole Life Instead */}
          <div className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <h3 className="text-2xl font-bold text-green-700 mb-2">🔄 Consider Whole Life Insurance Instead</h3>
              <p className="text-secondary-600">These situations may benefit more from whole life insurance</p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whoShouldConsiderWhole.map((candidate, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-green-50 border-2 border-green-200 rounded-xl p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="text-center mb-4">
                    <div className="bg-green-100 rounded-full p-3 w-16 h-16 mx-auto mb-3 flex items-center justify-center border border-green-200">
                      <SafeIcon icon={candidate.icon} className="h-8 w-8 text-green-600" />
                    </div>
                    <h4 className="font-bold text-secondary-900 mb-2">{candidate.title}</h4>
                    <p className="text-sm text-secondary-600 mb-3">{candidate.description}</p>
                  </div>
                  <div className="space-y-2">
                    <div className="bg-green-100 rounded-lg p-2">
                      <p className="text-xs font-semibold text-green-800">Why Whole Life:</p>
                      <p className="text-xs text-green-700">{candidate.reason}</p>
                    </div>
                    <div className="bg-blue-100 rounded-lg p-2">
                      <p className="text-xs font-semibold text-blue-800">Whole Life Benefit:</p>
                      <p className="text-xs text-blue-700">{candidate.alternative}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Who Should NOT Consider Final Expense */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <h3 className="text-2xl font-bold text-gray-700 mb-2">❌ Better Alternatives Available</h3>
              <p className="text-secondary-600">These situations typically have better, more cost-effective options</p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whoShouldNotConsider.map((candidate, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="text-center mb-4">
                    <div className="bg-gray-100 rounded-full p-3 w-16 h-16 mx-auto mb-3 flex items-center justify-center border border-gray-200">
                      <SafeIcon icon={candidate.icon} className="h-8 w-8 text-gray-600" />
                    </div>
                    <h4 className="font-bold text-secondary-900 mb-2">{candidate.title}</h4>
                    <p className="text-sm text-secondary-600 mb-3">{candidate.description}</p>
                  </div>
                  <div className="space-y-2">
                    <div className="bg-gray-100 rounded-lg p-2">
                      <p className="text-xs font-semibold text-gray-800">Why Skip:</p>
                      <p className="text-xs text-gray-700">{candidate.reason}</p>
                    </div>
                    <div className="bg-yellow-100 rounded-lg p-2">
                      <p className="text-xs font-semibold text-yellow-800">Better Option:</p>
                      <p className="text-xs text-yellow-700">{candidate.alternative}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Decision Helper */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 bg-white rounded-xl shadow-lg p-8"
          >
            <h3 className="text-2xl font-bold text-secondary-900 text-center mb-8">
              Quick Decision Guide
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-red-100 rounded-lg p-6 mb-4">
                  <SafeIcon icon={FiHeart} className="h-12 w-12 text-red-600 mx-auto mb-3" />
                  <h4 className="font-bold text-red-800 mb-2">Choose Final Expense If:</h4>
                  <ul className="text-sm text-red-700 space-y-1 text-left">
                    <li>• You're 50+ years old</li>
                    <li>• You have health issues</li>
                    <li>• You need $1,000-$30,000 coverage</li>
                    <li>• You want guaranteed acceptance</li>
                    <li>• You're on a fixed income</li>
                  </ul>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-green-100 rounded-lg p-6 mb-4">
                  <SafeIcon icon={FiShield} className="h-12 w-12 text-green-600 mx-auto mb-3" />
                  <h4 className="font-bold text-green-800 mb-2">Choose Whole Life If:</h4>
                  <ul className="text-sm text-green-700 space-y-1 text-left">
                    <li>• You need more than $30,000</li>
                    <li>• You want cash value growth</li>
                    <li>• You're planning your estate</li>
                    <li>• You're under 65 years old</li>
                    <li>• You want investment component</li>
                  </ul>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-blue-100 rounded-lg p-6 mb-4">
                  <SafeIcon icon={FiClock} className="h-12 w-12 text-blue-600 mx-auto mb-3" />
                  <h4 className="font-bold text-blue-800 mb-2">Choose Term Life If:</h4>
                  <ul className="text-sm text-blue-700 space-y-1 text-left">
                    <li>• You're under 50 years old</li>
                    <li>• You're healthy</li>
                    <li>• You need large coverage amounts</li>
                    <li>• You want lowest premiums</li>
                    <li>• You have dependents</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-secondary-900 mb-6">
                Why Final Expense Insurance?
              </h2>
              <p className="text-lg text-secondary-600 mb-8">
                Final expense insurance is specifically designed to cover end-of-life costs, ensuring your family doesn't face financial burden during an already difficult time.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-green-500 mr-3 mt-1" />
                    <span className="text-secondary-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-secondary-50 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-secondary-900 mb-6">Coverage Options</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-white rounded-lg border">
                  <div>
                    <h4 className="font-semibold text-secondary-900">Basic Coverage</h4>
                    <p className="text-sm text-secondary-600">$1,000 - $5,000</p>
                  </div>
                  <span className="text-red-600 font-bold">$15-40/mo</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-white rounded-lg border">
                  <div>
                    <h4 className="font-semibold text-secondary-900">Standard Coverage</h4>
                    <p className="text-sm text-secondary-600">$5,000 - $15,000</p>
                  </div>
                  <span className="text-red-600 font-bold">$40-90/mo</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-white rounded-lg border">
                  <div>
                    <h4 className="font-semibold text-secondary-900">Premium Coverage</h4>
                    <p className="text-sm text-secondary-600">$15,000 - $30,000</p>
                  </div>
                  <span className="text-red-600 font-bold">$90-150/mo</span>
                </div>
              </div>
              <p className="text-sm text-secondary-600 mt-4">
                *Rates vary by age, gender, and coverage amount
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-secondary-900 text-center mb-12">
            How Final Expense Insurance Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg p-6 text-center"
            >
              <div className="bg-red-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl font-bold text-red-600">1</span>
              </div>
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">Apply Online</h3>
              <p className="text-secondary-600">
                Simple application with no medical exam. Answer a few basic questions about your health.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-lg p-6 text-center"
            >
              <div className="bg-red-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl font-bold text-red-600">2</span>
              </div>
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">Get Approved</h3>
              <p className="text-secondary-600">
                Guaranteed acceptance for ages 50-85. Coverage begins immediately after first payment.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-lg p-6 text-center"
            >
              <div className="bg-red-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl font-bold text-red-600">3</span>
              </div>
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">Peace of Mind</h3>
              <p className="text-secondary-600">
                Your family receives the full benefit to cover final expenses and any remaining debts.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-secondary-900 text-center mb-12">
            What Our Final Expense Customers Say
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-secondary-50 rounded-lg p-6"
              >
                <blockquote className="text-secondary-700 mb-4 italic">
                  "{testimonial.quote}"
                </blockquote>
                <div className="border-t border-secondary-200 pt-4">
                  <p className="font-semibold text-secondary-900">{testimonial.name}</p>
                  <p className="text-sm text-secondary-600">{testimonial.location}</p>
                  <p className="text-sm text-red-600 font-medium mt-1">{testimonial.coverage}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-red-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Protect Your Family from Final Expense Burden
          </h2>
          <p className="text-xl text-red-100 mb-8">
            Guaranteed acceptance final expense insurance. No medical exam, immediate coverage.
          </p>
          <a
            href="https://quote.burbankinsuranceservices.com/Dt2tCn"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-300 transition-colors"
          >
            Get My Price
            <SafeIcon icon={FiArrowRight} className="h-5 w-5 ml-2" />
          </a>
          <p className="mt-4 text-sm text-red-200">
            Ages 50-85 automatically approved • Coverage starts immediately
          </p>
        </div>
      </section>

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default FinalExpensePage;